import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Building2, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';
import { Case } from '../types';
import Button from '../components/ui/Button';
import Card, { CardHeader, CardBody, CardFooter } from '../components/ui/Card';
import Input from '../components/ui/Input';
import Textarea from '../components/ui/Textarea';
import Spinner from '../components/ui/Spinner';
import Modal from '../components/ui/Modal';
import Select from '../components/ui/Select';
import BusinessDomainForm from '../components/forms/BusinessDomainForm';
import ClientDetailsForm, { SelectedCompany } from '../components/forms/ClientDetailsForm';
import SystemLandscapeForm from '../components/forms/SystemLandscapeForm';
import DataFilesForm from '../components/forms/DataFilesForm';
import ExecutiveNeedsForm from '../components/forms/ExecutiveNeedsForm';
import DataRequirementsForm from '../components/forms/DataRequirementsForm';
import CompanyResearchReview from '../components/forms/CompanyResearchReview';
import IndustryResearchForm from '../components/forms/IndustryResearchForm';
import CompetitorAnalysisForm from '../components/forms/CompetitorAnalysisForm';
import ValueTreeBuilder from '../components/forms/ValueTreeBuilder';
import PersonaManager from '../components/forms/PersonaManager';

const STEPS = [
  { id: 1, name: 'Case Details', description: 'Name, description, and company selection' },
  { id: 2, name: 'Company Overview', description: 'Review selected company details' },
  { id: 3, name: 'Business Domain', description: 'Define your business context' },
  { id: 4, name: 'Executive Needs', description: 'Define stakeholder requirements' },
  { id: 5, name: 'Discovery Phase', description: 'Industry research, competitors, value tree, and personas' },
  { id: 6, name: 'Data Requirements', description: 'Business Transformation Studio analysis of required data' },
  { id: 7, name: 'System Landscape', description: 'Map your enterprise systems' },
];

// Helper function to format research data into readable text
function formatResearchData(data: any): string {
  const sections: string[] = [];

  // Financial Profile
  if (data.financialProfile) {
    sections.push('FINANCIAL PROFILE:');
    if (data.financialProfile.revenue?.value) {
      sections.push(`• Revenue: ${data.financialProfile.revenue.value} (${data.financialProfile.revenue.period || 'N/A'})`);
      if (data.financialProfile.revenue.source) {
        sections.push(`  Source: ${data.financialProfile.revenue.source}`);
      }
    }
    if (data.financialProfile.revenueBySegment?.length > 0) {
      sections.push(`• Revenue by Segment:`);
      data.financialProfile.revenueBySegment.forEach((seg: any) => {
        sections.push(`  - ${seg.segment}: ${seg.percentage}%`);
      });
    }
    if (data.financialProfile.keyDrivers?.length > 0) {
      sections.push(`• Key Drivers: ${data.financialProfile.keyDrivers.join(', ')}`);
    }
    sections.push('');
  }

  // Business Structure
  if (data.businessStructure) {
    sections.push('BUSINESS STRUCTURE:');
    if (data.businessStructure.businessUnits?.length > 0) {
      sections.push(`• Business Units: ${data.businessStructure.businessUnits.join(', ')}`);
    }
    if (data.businessStructure.productLines?.length > 0) {
      sections.push(`• Product Lines: ${data.businessStructure.productLines.join(', ')}`);
    }
    if (data.businessStructure.geographicFootprint?.length > 0) {
      sections.push(`• Geographic Footprint: ${data.businessStructure.geographicFootprint.join(', ')}`);
    }
    sections.push('');
  }

  // Organizational Scale
  if (data.organizationalScale) {
    sections.push('ORGANIZATIONAL SCALE:');
    if (data.organizationalScale.employeeCount?.value) {
      sections.push(`• Employees: ${data.organizationalScale.employeeCount.value}`);
    }
    if (data.organizationalScale.facilities?.value) {
      sections.push(`• Facilities: ${data.organizationalScale.facilities.value}`);
    }
    if (data.organizationalScale.customerBase) {
      sections.push(`• Customer Base: ${data.organizationalScale.customerBase}`);
    }
    sections.push('');
  }

  // Industry Context
  if (data.industryContext) {
    sections.push('INDUSTRY CONTEXT:');
    if (data.industryContext.vertical) {
      sections.push(`• Industry: ${data.industryContext.vertical}`);
    }
    if (data.industryContext.subSector) {
      sections.push(`• Sub-sector: ${data.industryContext.subSector}`);
    }
    if (data.industryContext.regulations?.length > 0) {
      sections.push(`• Regulations: ${data.industryContext.regulations.join(', ')}`);
    }
    sections.push('');
  }

  // Strategic Priorities
  if (data.strategicPriorities) {
    sections.push('STRATEGIC PRIORITIES:');
    if (data.strategicPriorities.growthInitiatives?.length > 0) {
      sections.push(`• Growth Initiatives:`);
      data.strategicPriorities.growthInitiatives.forEach((init: string) => {
        sections.push(`  - ${init}`);
      });
    }
    if (data.strategicPriorities.challenges?.length > 0) {
      sections.push(`• Challenges:`);
      data.strategicPriorities.challenges.forEach((challenge: string) => {
        sections.push(`  - ${challenge}`);
      });
    }
    sections.push('');
  }

  // Data Complexity
  if (data.dataComplexity) {
    sections.push('DATA COMPLEXITY:');
    if (data.dataComplexity.scope) {
      sections.push(`• Scope: ${data.dataComplexity.scope}`);
    }
    if (data.dataComplexity.productComplexity) {
      sections.push(`• Product Complexity: ${data.dataComplexity.productComplexity}`);
    }
    sections.push('');
  }

  // Sources
  if (data.sources?.length > 0) {
    sections.push('SOURCES:');
    data.sources.forEach((source: string, idx: number) => {
      sections.push(`${idx + 1}. ${source}`);
    });
  }

  return sections.join('\n');
}

export default function NewCase() {
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId: string }>();
  const isEditMode = !!caseId;

  const [currentStep, setCurrentStep] = useState(1);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [createdProjectId, setCreatedProjectId] = useState<string | null>(caseId || null);
  const [businessDomain, setBusinessDomain] = useState('');
  const [businessDomainContext, setBusinessDomainContext] = useState('');

  // Discovery Phase tab state
  const [discoveryTab, setDiscoveryTab] = useState<'industry' | 'competitors' | 'valueTree' | 'personas'>('industry');

  // Company selection state
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<any>(null);

  // Legacy state (to be deprecated)
  const [clientName, setClientName] = useState('');
  const [clientWebsiteUrl, setClientWebsiteUrl] = useState('');
  const [clientDetails, setClientDetails] = useState('');
  const [systemLandscapeData, setSystemLandscapeData] = useState<any[]>([]);
  const [dataFilesData, setDataFilesData] = useState<any[]>([]);
  const [executiveNeedsData, setExecutiveNeedsData] = useState<any[]>([]);

  // Company research modal state
  const [showResearchModal, setShowResearchModal] = useState(false);
  const [researchData, setResearchData] = useState<any>(null);
  const [isSavingResearch, setIsSavingResearch] = useState(false);
  const [selectedCompanyCik, setSelectedCompanyCik] = useState<string>('');
  const [selectedCompanyTicker, setSelectedCompanyTicker] = useState<string>('');
  const [isManualCompanyEntry, setIsManualCompanyEntry] = useState(false);
  const [savedCompanyId, setSavedCompanyId] = useState<string | null>(null);

  // Store original values to detect changes
  const [originalName, setOriginalName] = useState('');
  const [originalDescription, setOriginalDescription] = useState('');
  const [originalBusinessDomain, setOriginalBusinessDomain] = useState('');
  const [originalBusinessDomainContext, setOriginalBusinessDomainContext] = useState('');
  const [originalClientName, setOriginalClientName] = useState('');
  const [originalClientWebsiteUrl, setOriginalClientWebsiteUrl] = useState('');
  const [originalClientDetails, setOriginalClientDetails] = useState('');

  // Fetch existing project data if in edit mode
  const { data: existingCase, isLoading: isLoadingCase } = useQuery<Case>({
    queryKey: ['case', caseId],
    queryFn: () => api.get<Case>(`/cases/${caseId}`),
    enabled: isEditMode,
  });

  // Fetch existing business domain and context if project exists
  const { data: existingBusinessDomainData, isLoading: isLoadingBusinessDomain, isError } = useQuery({
    queryKey: ['businessDomain', createdProjectId || caseId],
    queryFn: async () => {
      try {
        return await api.get(`/cases/${createdProjectId || caseId}/business-domain`);
      } catch (error: any) {
        // Return null if business domain doesn't exist (404), otherwise throw
        if (error?.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    enabled: !!(createdProjectId || caseId),
    retry: false, // Don't retry if 404 (business domain doesn't exist yet)
    throwOnError: false, // Don't throw errors to error boundary
  });

  // Fetch standalone companies for company selection
  const { data: standaloneCompanies, isLoading: isLoadingCompanies, error: companiesError } = useQuery({
    queryKey: ['standalone-companies'],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/companies`);
      if (!response.ok) {
        throw new Error('Failed to fetch companies');
      }
      return response.json();
    },
  });

  // Ensure standaloneCompanies is always an array
  const companies = standaloneCompanies || [];

  // Load existing project data into form
  useEffect(() => {
    if (existingCase && isEditMode) {
      setProjectName(existingCase.name);
      setProjectDescription(existingCase.description || '');
      setOriginalName(existingCase.name);
      setOriginalDescription(existingCase.description || '');
      setCreatedProjectId(existingCase.caseId || existingCase.id);

      // Load company selection if exists
      if (existingCase.companyId) {
        setSelectedCompanyId(existingCase.companyId);
        // Find and set the full company object from the companies list
        if (companies.length > 0) {
          const company = companies.find((c: any) => c.companyId === existingCase.companyId);
          if (company) {
            setSelectedCompany(company);
          }
        }
      }
    }
  }, [existingCase, isEditMode, companies]);

  // Load existing business domain data
  useEffect(() => {
    if (existingBusinessDomainData) {
      setBusinessDomain(existingBusinessDomainData.domain || '');
      setBusinessDomainContext(existingBusinessDomainData.context || '');
      setOriginalBusinessDomain(existingBusinessDomainData.domain || '');
      setOriginalBusinessDomainContext(existingBusinessDomainData.context || '');
    }
  }, [existingBusinessDomainData]);

  const createProjectMutation = useMutation({
    mutationFn: (projectData: any) =>
      api.post<Case>('/cases', projectData),
    onSuccess: (project) => {
      toast.success('Case created successfully!');
      setCreatedProjectId(project.caseId || project.id);
      // Store original values after creation
      setOriginalName(projectName.trim());
      setOriginalDescription(projectDescription.trim());
      // Move to next step after project is created
      setCurrentStep(currentStep + 1);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to create project');
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: (projectData: any) =>
      api.patch<Case>(`/cases/${createdProjectId}`, projectData),
    onSuccess: () => {
      toast.success('Case updated successfully!');
      // Update original values after successful update
      setOriginalName(projectName.trim());
      setOriginalDescription(projectDescription.trim());
      // Move to next step after project is updated
      setCurrentStep(currentStep + 1);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update project');
    },
  });

  const createBusinessDomainMutation = useMutation({
    mutationFn: (data: { domain: string; context: string }) =>
      api.post(`/cases/${createdProjectId}/business-domain`, data),
    onSuccess: () => {
      toast.success('Business domain created successfully!');
      setOriginalBusinessDomain(businessDomain.trim());
      setOriginalBusinessDomainContext(businessDomainContext.trim());
      // Move to next step after business domain is created
      setCurrentStep(currentStep + 1);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to create business domain');
    },
  });

  const updateBusinessDomainMutation = useMutation({
    mutationFn: (data: { domain: string; context: string }) =>
      api.patch(`/cases/${createdProjectId}/business-domain`, data),
    onSuccess: () => {
      toast.success('Business domain updated successfully!');
      setOriginalBusinessDomain(businessDomain.trim());
      setOriginalBusinessDomainContext(businessDomainContext.trim());
      // Move to next step after business domain is updated
      setCurrentStep(currentStep + 1);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to update business domain');
    },
  });

  const createManualCompanyMutation = useMutation({
    mutationFn: (data: { name: string; websiteUrl: string | null; researchData: {} }) =>
      api.post(`/company-research/cases/${createdProjectId}/company`, data),
    onSuccess: () => {
      toast.success('Company saved successfully!');
      setOriginalClientName(clientName.trim());
      setOriginalClientWebsiteUrl(clientWebsiteUrl.trim());
      // Move to next step after company is saved
      setCurrentStep(currentStep + 1);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || error?.response?.data?.message || 'Failed to save company');
    },
  });

  const updateManualCompanyMutation = useMutation({
    mutationFn: (data: { name: string; websiteUrl: string | null; researchData: {} }) =>
      api.patch(`/company-research/cases/${createdProjectId}/company`, data),
    onSuccess: () => {
      toast.success('Company updated successfully!');
      setOriginalClientName(clientName.trim());
      setOriginalClientWebsiteUrl(clientWebsiteUrl.trim());
      // Move to next step after company is updated
      setCurrentStep(currentStep + 1);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || error?.response?.data?.message || 'Failed to update company');
    },
  });

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Link company to legacy project mutation
  const linkCompanyMutation = useMutation({
    mutationFn: ({ caseId, companyId }: { caseId: string; companyId: string }) =>
      api.post(`/cases/${caseId}/link-company`, { company_id: companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case', createdProjectId] });
      toast.success('Company linked to project successfully!');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || error?.message || 'Failed to link company');
    },
  });

  const handleNext = () => {
    // Validate and create/update project on step 1
    if (currentStep === 1) {
      if (!projectName.trim()) {
        toast.error('Please enter a case ID to continue');
        return;
      }
      if (!projectDescription.trim()) {
        toast.error('Please enter a project description to continue');
        return;
      }
      if (!selectedCompanyId) {
        toast.error('Please select a company to continue');
        return;
      }

      // Check if there are any changes
      const hasChanges =
        projectName.trim() !== originalName ||
        projectDescription.trim() !== originalDescription;

      // If in edit mode or project already created
      if (isEditMode || createdProjectId) {
        // Only update if there are changes
        if (hasChanges) {
          const projectData = {
            name: projectName.trim(),
            description: projectDescription.trim(),
          };
          updateProjectMutation.mutate(projectData);
        } else {
          // No changes, just move to next step
          setCurrentStep(currentStep + 1);
        }
      } else {
        // Create new project with company reference
        const projectData = {
          name: projectName.trim(),
          description: projectDescription.trim(),
          companyId: selectedCompanyId,
        };
        createProjectMutation.mutate(projectData);
      }
      return; // Don't move to next step yet - mutation onSuccess will handle it (or we already moved)
    }

    // Step 2: Company Overview - just a review step, no validation needed
    if (currentStep === 2) {
      // Simply move to next step
      setCurrentStep(currentStep + 1);
      return;
    }

    // Validate and create/update business domain on step 3
    if (currentStep === 3) {
      if (!businessDomain.trim()) {
        toast.error('Please select a business domain to continue');
        return;
      }
      if (!businessDomainContext.trim()) {
        toast.error('Please enter business domain context to continue');
        return;
      }

      // Check if there are any changes
      const hasChanges =
        businessDomain.trim() !== originalBusinessDomain ||
        businessDomainContext.trim() !== originalBusinessDomainContext;

      // If business domain already exists
      if (existingBusinessDomainData) {
        // Only update if there are changes
        if (hasChanges) {
          const businessDomainData = {
            domain: businessDomain.trim(),
            context: businessDomainContext.trim(),
          };
          updateBusinessDomainMutation.mutate(businessDomainData);
        } else {
          // No changes, just move to next step
          setCurrentStep(currentStep + 1);
        }
      } else {
        // Create new business domain
        const businessDomainData = {
          domain: businessDomain.trim(),
          context: businessDomainContext.trim(),
        };
        createBusinessDomainMutation.mutate(businessDomainData);
      }
      return; // Don't move to next step yet - mutation onSuccess will handle it (or we already moved)
    }

    // For other steps, just move forward
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    // Allow going back to any previous step
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  const handleSubmit = () => {
    if (!createdProjectId) {
      toast.error('Case not created yet. Please go back to Step 1.');
      return;
    }

    // Navigate to the created project
    // TODO: Save additional form data (businessDomain, systemLandscape, etc.) before navigating
    toast.success('Case setup complete!');
    navigate(`/cases/${createdProjectId}`);
  };

  // Show loading spinner while fetching existing project (not for business domain - that's optional)
  if (isEditMode && isLoadingCase) {
    return (
      <div className="max-w-5xl mx-auto flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={handleCancel}
          className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <h1 className="text-[40px] font-bold text-neutral-900 mb-2">
          {isEditMode ? projectName : (projectName ? projectName : 'Create New Case')}
        </h1>
        <p className="text-neutral-600">
          {isEditMode ? (
            <>
              Editing project (ID: {createdProjectId}) - Update your project details
            </>
          ) : createdProjectId ? (
            <>
              Project created successfully! (ID: {createdProjectId}) - Continue configuring your project
            </>
          ) : projectName ? (
            'Follow the steps to define your project requirements'
          ) : (
            'Start by naming your project and providing a description'
          )}
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-start flex-1">
              <div className="flex flex-col items-center w-full">
                {/* Step Circle */}
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold mb-3 transition-colors flex-shrink-0 ${
                    currentStep > step.id
                      ? 'bg-secondary-500 text-white'
                      : currentStep === step.id
                      ? 'bg-secondary-500 text-white ring-4 ring-secondary-100'
                      : 'bg-neutral-200 text-neutral-600'
                  }`}
                >
                  {currentStep > step.id ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    step.id
                  )}
                </div>
                {/* Step Label */}
                <div className="text-center w-full">
                  <p
                    className={`text-sm font-medium mb-1 whitespace-nowrap ${
                      currentStep >= step.id
                        ? 'text-neutral-900'
                        : 'text-neutral-500'
                    }`}
                  >
                    {step.name}
                  </p>
                  <p className="text-xs text-neutral-500 whitespace-nowrap">
                    {step.description}
                  </p>
                </div>
              </div>
              {/* Connector Line */}
              {index < STEPS.length - 1 && (
                <div
                  className={`h-0.5 flex-1 mx-2 mt-5 transition-colors ${
                    currentStep > step.id ? 'bg-secondary-500' : 'bg-neutral-200'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <Card>
        <CardHeader>
          <h2 className="text-2xl font-bold text-neutral-900">
            {STEPS[currentStep - 1].name}
          </h2>
          <p className="text-neutral-600 mt-1">
            {STEPS[currentStep - 1].description}
          </p>
        </CardHeader>
        <CardBody className="min-h-[400px]">
          {/* Step 1: Case Details */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <p className="text-neutral-600">
                Give your project a name, description, and select the company you want to analyze.
              </p>

              <Input
                label="Case Name"
                placeholder="e.g., Tesla Financial Analysis Q4 2024"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                helperText="Choose a descriptive name for your project"
                required
                autoFocus
              />

              <Textarea
                label="Case Description"
                placeholder="Brief description of what this analysis will cover..."
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                rows={3}
                helperText="Add a short description to help team members understand the project goals"
                required
              />

              {/* Company Selection */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-neutral-700">
                  Select Company/Client <span className="text-error">*</span>
                </label>
                <Select
                  value={selectedCompanyId}
                  onChange={(e) => {
                    const companyId = e.target.value;
                    setSelectedCompanyId(companyId);
                    const company = companies.find((c: any) => c.companyId === companyId);
                    setSelectedCompany(company || null);

                    // If editing existing project without company, link it now
                    if (createdProjectId && !existingCase?.companyId && companyId) {
                      linkCompanyMutation.mutate({ caseId: createdProjectId, companyId });
                    }
                  }}
                  disabled={isLoadingCompanies || (!!createdProjectId && !!existingCase?.companyId)}
                >
                  <option value="">
                    {isLoadingCompanies ? 'Loading companies...' : companiesError ? 'Error loading companies' : 'Choose from researched companies...'}
                  </option>
                  {companies.map((company: any) => (
                    <option key={company.companyId} value={company.companyId}>
                      {company.companyName} {company.ticker ? `(${company.ticker})` : ''}
                    </option>
                  ))}
                </Select>
                {createdProjectId && selectedCompanyId && existingCase?.companyId && (
                  <p className="text-sm text-neutral-600">
                    Company selection cannot be changed after project creation
                  </p>
                )}
                {createdProjectId && !existingCase?.companyId && (
                  <p className="text-sm text-amber-600">
                    This project doesn't have a company. Select one to link it.
                  </p>
                )}
                {companiesError ? (
                  <p className="text-sm text-error">
                    Failed to load companies. Please ensure the backend server is running.
                  </p>
                ) : (
                  <p className="text-sm text-neutral-500">
                    Select a company from your standalone research. Don't see your company?
                    <button
                      type="button"
                      onClick={() => window.open('/companies/new', '_blank')}
                      className="ml-1 text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
                    >
                      Research new company
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </p>
                )}
              </div>

              {/* Company Preview Card */}
              {selectedCompany && (
                <div className="mt-6 border border-neutral-200 rounded-lg p-5 bg-neutral-50">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-primary-100 rounded-lg">
                      <Building2 className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-semibold text-neutral-900">
                        {selectedCompany.companyName}
                      </h4>
                      {selectedCompany.ticker && (
                        <p className="text-sm text-neutral-600 mt-1">
                          Ticker: <span className="font-medium">{selectedCompany.ticker}</span>
                          {selectedCompany.cik && ` • CIK: ${selectedCompany.cik}`}
                        </p>
                      )}
                      <div className="mt-3 grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-neutral-500">Reports</p>
                          <p className="font-medium text-neutral-900">{selectedCompany.reportCount || 0}</p>
                        </div>
                        {selectedCompany.latestRevenue && (
                          <div>
                            <p className="text-neutral-500">Latest Revenue</p>
                            <p className="font-medium text-neutral-900">{selectedCompany.latestRevenue}</p>
                          </div>
                        )}
                        {selectedCompany.latestPeriod && (
                          <div>
                            <p className="text-neutral-500">Period</p>
                            <p className="font-medium text-neutral-900">{selectedCompany.latestPeriod}</p>
                          </div>
                        )}
                      </div>
                      <div className="mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.open(`/companies/${selectedCompany.companyId}`, '_blank')}
                        >
                          View Full Details →
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Company Overview */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <p className="text-neutral-600">
                Review the company details for this project. This data comes from your standalone company research.
              </p>

              {selectedCompany ? (
                <div className="space-y-6">
                  {/* Company Header */}
                  <div className="border border-neutral-200 rounded-lg p-6 bg-white">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="p-4 bg-primary-100 rounded-lg">
                          <Building2 className="w-8 h-8 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-neutral-900">
                            {selectedCompany.companyName}
                          </h3>
                          {selectedCompany.ticker && (
                            <p className="text-neutral-600 mt-1">
                              Ticker: <span className="font-semibold">{selectedCompany.ticker}</span>
                              {selectedCompany.cik && ` • CIK: ${selectedCompany.cik}`}
                            </p>
                          )}
                          <p className="text-sm text-neutral-500 mt-2">
                            Source: {selectedCompany.source || 'SEC Filings'} •
                            Last researched: {selectedCompany.lastResearchedAt ? new Date(selectedCompany.lastResearchedAt).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/companies/${selectedCompany.companyId}`, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Full Details
                      </Button>
                    </div>
                  </div>

                  {/* Company Metrics */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="border border-neutral-200 rounded-lg p-4 bg-white">
                      <p className="text-sm text-neutral-500 mb-1">Reports Available</p>
                      <p className="text-2xl font-bold text-neutral-900">{selectedCompany.reportCount || 0}</p>
                    </div>
                    {selectedCompany.latestRevenue && (
                      <div className="border border-neutral-200 rounded-lg p-4 bg-white">
                        <p className="text-sm text-neutral-500 mb-1">Latest Revenue</p>
                        <p className="text-2xl font-bold text-neutral-900">{selectedCompany.latestRevenue}</p>
                      </div>
                    )}
                    {selectedCompany.latestPeriod && (
                      <div className="border border-neutral-200 rounded-lg p-4 bg-white">
                        <p className="text-sm text-neutral-500 mb-1">Latest Period</p>
                        <p className="text-2xl font-bold text-neutral-900">{selectedCompany.latestPeriod}</p>
                      </div>
                    )}
                  </div>

                  {/* Info Alert */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0">
                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-blue-900 mb-1">Company Data Snapshot</h4>
                        <p className="text-sm text-blue-800">
                          This project will use a snapshot of {selectedCompany.companyName}'s data from the standalone research module.
                          You can refresh company data later from the project's company details page if new reports are filed.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => window.open('/companies/new', '_blank')}
                      className="flex-1"
                    >
                      <Building2 className="w-4 h-4 mr-2" />
                      Research Different Company
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setCurrentStep(1)}
                      className="flex-1"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Change Selection
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Building2 className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-neutral-900 mb-2">No Company Selected</h3>
                  <p className="text-neutral-600 mb-4">Please go back to Step 1 and select a company.</p>
                  <Button onClick={() => setCurrentStep(1)}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Go to Step 1
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Business Domain */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <p className="text-neutral-600">
                Select your business domain and describe the project scope to help generate a tailored graph solution.
              </p>
              <BusinessDomainForm
                domain={businessDomain}
                context={businessDomainContext}
                onDomainChange={setBusinessDomain}
                onContextChange={setBusinessDomainContext}
              />
            </div>
          )}

          {/* Step 4: Executive Needs */}
          {currentStep === 4 && (
            <ExecutiveNeedsForm
              caseId={createdProjectId || undefined}
              onSave={setExecutiveNeedsData}
            />
          )}

          {/* Step 5: Discovery Phase - NEW */}
          {currentStep === 5 && (
            <div className="space-y-6">
              {/* Tabbed Interface for Discovery Sub-phases */}
              <div className="border-b border-neutral-200">
                <nav className="flex gap-4">
                  <button
                    className={`px-4 py-2 border-b-2 font-medium transition-colors ${
                      discoveryTab === 'industry'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-neutral-600 hover:text-neutral-900'
                    }`}
                    onClick={() => setDiscoveryTab('industry')}
                  >
                    Industry Research
                  </button>
                  <button
                    className={`px-4 py-2 border-b-2 font-medium transition-colors ${
                      discoveryTab === 'competitors'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-neutral-600 hover:text-neutral-900'
                    }`}
                    onClick={() => setDiscoveryTab('competitors')}
                  >
                    Competitor Analysis
                  </button>
                  <button
                    className={`px-4 py-2 border-b-2 font-medium transition-colors ${
                      discoveryTab === 'valueTree'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-neutral-600 hover:text-neutral-900'
                    }`}
                    onClick={() => setDiscoveryTab('valueTree')}
                  >
                    Value Tree
                  </button>
                  <button
                    className={`px-4 py-2 border-b-2 font-medium transition-colors ${
                      discoveryTab === 'personas'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-neutral-600 hover:text-neutral-900'
                    }`}
                    onClick={() => setDiscoveryTab('personas')}
                  >
                    Personas & Questions
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              {discoveryTab === 'industry' && createdProjectId && (
                <IndustryResearchForm
                  caseId={createdProjectId}
                  onSubmit={async (data) => {
                    await api.post(`/cases/${createdProjectId}/industry`, data);
                  }}
                />
              )}

              {discoveryTab === 'competitors' && createdProjectId && (
                <CompetitorAnalysisForm caseId={createdProjectId} />
              )}

              {discoveryTab === 'valueTree' && createdProjectId && (
                <ValueTreeBuilder caseId={createdProjectId} />
              )}

              {discoveryTab === 'personas' && createdProjectId && (
                <PersonaManager caseId={createdProjectId} />
              )}
            </div>
          )}

          {/* Step 6: Data Requirements (was step 5) */}
          {currentStep === 6 && (
            <DataRequirementsForm
              caseId={createdProjectId || undefined}
            />
          )}

          {/* Step 7: System Landscape (was step 6) */}
          {currentStep === 7 && (
            <SystemLandscapeForm
              caseId={createdProjectId || undefined}
              onSave={setSystemLandscapeData}
            />
          )}
        </CardBody>
        <CardFooter>
          <div className="flex items-center justify-between w-full">
            <Button
              variant="outline"
              onClick={currentStep === 1 ? handleCancel : handlePrevious}
            >
              <ArrowLeft className="w-4 h-4" />
              {currentStep === 1 ? 'Cancel' : 'Previous'}
            </Button>
            {currentStep < STEPS.length ? (
              <Button
                onClick={handleNext}
                loading={
                  (currentStep === 1 && (createProjectMutation.isPending || updateProjectMutation.isPending)) ||
                  (currentStep === 2 && (createManualCompanyMutation.isPending || updateManualCompanyMutation.isPending)) ||
                  (currentStep === 3 && (createBusinessDomainMutation.isPending || updateBusinessDomainMutation.isPending))
                }
                disabled={
                  (currentStep === 1 && (createProjectMutation.isPending || updateProjectMutation.isPending)) ||
                  (currentStep === 2 && (createManualCompanyMutation.isPending || updateManualCompanyMutation.isPending)) ||
                  (currentStep === 3 && (createBusinessDomainMutation.isPending || updateBusinessDomainMutation.isPending))
                }
              >
                {currentStep === 1 && (createProjectMutation.isPending || updateProjectMutation.isPending)
                  ? (isEditMode ? 'Updating Project...' : 'Creating Project...')
                  : currentStep === 2 && (createManualCompanyMutation.isPending || updateManualCompanyMutation.isPending)
                  ? 'Saving Company...'
                  : currentStep === 3 && (createBusinessDomainMutation.isPending || updateBusinessDomainMutation.isPending)
                  ? (existingBusinessDomainData ? 'Updating Business Domain...' : 'Creating Business Domain...')
                  : 'Next'}
                <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button
                onClick={() => {
                  if (!createdProjectId) {
                    toast.error('Case ID not found');
                    return;
                  }
                  // Navigate to summary page for review and analysis
                  navigate(`/cases/${createdProjectId}/summary`);
                }}
                disabled={!createdProjectId}
              >
                <Check className="w-4 h-4" />
                Review & Analyse
              </Button>
            )}
          </div>
        </CardFooter>
      </Card>

      {/* Company Research Review Modal */}
      <Modal
        isOpen={showResearchModal}
        onClose={() => {
          setShowResearchModal(false);
          setResearchData(null);
        }}
        title="Review Company Research"
        size="xl"
      >
        {researchData && (
          <CompanyResearchReview
            researchData={researchData}
            isSaving={isSavingResearch}
            caseId={createdProjectId || undefined}
            companyId={savedCompanyId || undefined}
            onCancel={() => {
              setShowResearchModal(false);
              setResearchData(null);
              setSavedCompanyId(null);
            }}
            onSave={async (editedData) => {
              try {
                setIsSavingResearch(true);
                toast.loading('Saving research data...', { id: 'save-research' });

                // Check if company already exists
                let companyExists = false;
                try {
                  await api.get(`/company-research/cases/${createdProjectId}/company`);
                  companyExists = true;
                } catch (error: any) {
                  if (error?.response?.status !== 404) {
                    throw error;
                  }
                }

                // Create or update company with research data
                const companyData = {
                  name: clientName,
                  websiteUrl: clientWebsiteUrl || null,
                  cik: selectedCompanyCik || null,
                  ticker: selectedCompanyTicker || null,
                  researchData: editedData
                };

                let response: any;
                if (companyExists) {
                  response = await api.patch(`/company-research/cases/${createdProjectId}/company`, companyData);
                } else {
                  response = await api.post(`/company-research/cases/${createdProjectId}/company`, companyData);
                }

                // Capture companyId from response for timeline
                if (response?.company?.companyId) {
                  setSavedCompanyId(response.company.companyId);
                  console.log('✅ Captured companyId for timeline:', response.company.companyId);
                }

                // Format the research data into the details textarea for display
                const formatted = formatResearchData(editedData);
                setClientDetails(formatted);

                toast.success('Company research saved successfully!', { id: 'save-research' });

                // Keep modal open to show timeline with historical reports
                // User can close it manually
                // setShowResearchModal(false);
                // setResearchData(null);
              } catch (error: any) {
                toast.error(error?.response?.data?.detail || 'Failed to save research data', { id: 'save-research' });
              } finally {
                setIsSavingResearch(false);
              }
            }}
          />
        )}
      </Modal>

    </div>
  );
}
